import { http } from 'src/boot/axios'

/**
 * transport.js — la UNICA costura de red de offline-v2 (PUSH-05, espiritu STOR-05).
 *
 * Ningun otro archivo de la capa sync/ importa `axios`/`boot/axios`: todos pasan por aqui.
 * El transport por defecto es el `http` real (instancia axios de src/boot/axios.js, con los
 * interceptores de auth ya cableados: adjunta el Bearer `gql_access_token` para GRAPHQL_URL).
 * Los tests inyectan un fake via setTransport() (o pasan el transport explicito al handler) para
 * no tocar la red real.
 *
 * Espeja la ergonomia `{ db = StorageAdapter }` de F2 pero a nivel de modulo: `_transport ?? http`.
 * Surface que usaran los handlers de 04-02: transport.post(url, data, config) / .put(...) /
 * .delete(url, config). Aqui NO hay logica de request — es un passthrough fino a axios.
 */

let _transport = null

// TIMEOUT DEFAULT (hardening e2e 2026-06-10): sin timeout, un backend colgado (visto en vivo:
// pool FPM agotado) deja la request — y con ella TODO el tick del sync-worker — colgada para
// siempre. 30s default; cualquier config explicita del caller lo puede subir/bajar. Un timeout
// de axios lanza ECONNABORTED SIN .status -> classify lo trata como retry TRANSITORIO con
// backoff (nunca dead-letter), que es exactamente la semantica correcta para "backend lento".
// 120s: el e2e contra backend lento midio paginas de catalogo de ~135s con timeout de 30s ->
// el pull moria siempre. 120s cubre push/interactivo en redes de campo; los pulls de catalogos
// (payloads grandes) pasan su propio timeout explicito mas generoso (ver catalogs.js).
const DEFAULT_TIMEOUT_MS = 120000

// suppressConnectionToast: TODO el tráfico de esta capa es de fondo con reintentos propios
// (drain con backoff + pulls best-effort) y señalización durable (TheSyncBanner/TheSyncDrawer).
// Sin esto, el interceptor global (src/api/responser.js) toastea "Error de conexión" por cada
// intento transitorio fallido — falsa alarma en device: la captura ya quedó durable en SQLite
// y el outbox la resube sola. Un caller puede revertirlo por request con el flag en false.
function withDefaultTimeout (t) {
  const cfg = (config) => ({ timeout: DEFAULT_TIMEOUT_MS, suppressConnectionToast: true, ...(config || {}) })
  return {
    get: (url, config) => t.get(url, cfg(config)),
    post: (url, data, config) => t.post(url, data, cfg(config)),
    put: (url, data, config) => t.put(url, data, cfg(config)),
    delete: (url, config) => t.delete(url, cfg(config))
  }
}

/** Devuelve el transport activo: el fake inyectado (tal cual, sin wrapper — los tests
 *  controlan su propio timing), o el `http` real envuelto con el timeout default. */
export function getTransport () {
  return _transport ?? withDefaultTimeout(http)
}

/** Inyecta un transport fake (tests) o un wrapper alternativo. */
export function setTransport (fake) {
  _transport = fake
}

/** Restablece el transport por defecto (`http`). Para teardown de tests. */
export function resetTransport () {
  _transport = null
}
